import { useTraderText } from '../context/TraderLanguageContext';
/**
 * Market Watchlist Component
 * Left panel with instrument list, search, and 1-Click Trading toggle
 * Uses extracted WatchlistContent for scroll stability
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { orderIntent } from '../utils/orderIntent';
import { useTraderTheme } from '../context/TraderThemeContext';
import { useTraderLanguage } from '../context/TraderLanguageContext';
import { useMobileNav } from '../context/MobileNavContext';
import { useWebSocket } from '../../context/WebSocketContext';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import WatchlistContent from './WatchlistContent';
import { scopedQuote } from '../utils/quotes';
import { isBinancePair } from '../utils/binance';
import { useMarketClock } from '../hooks/useBinanceHistory';
import { watchLiveRefresh } from '../../hooks/liveRefresh';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Sample fallback instruments
const fallbackInstruments = [
  { symbol: 'EUR/USD', bid: 1.0842, ask: 1.0845, change: 0.15, category: 'forex' },
  { symbol: 'GBP/USD', bid: 1.2634, ask: 1.2637, change: -0.22, category: 'forex' },
  { symbol: 'USD/JPY', bid: 149.52, ask: 149.55, change: 0.08, category: 'forex' },
  { symbol: 'XAU/USD', bid: 2175.50, ask: 2176.20, change: 1.25, category: 'commodities' },
  { symbol: 'BTC/USD', bid: 67250.00, ask: 67350.00, change: 2.35, category: 'crypto' },
];

const MarketWatchlist = ({ onSelectInstrument, selectedInstrument, onInstrumentClick }) => {
  const tx = useTraderText();
  const orderKey=useRef(null),tradeBusy=useRef(false);
  const { isDark } = useTraderTheme();
  const { t } = useTraderLanguage();
  const { watchlistOpen, setWatchlistOpen, isMobile, isTablet } = useMobileNav();
  const { prices, subscribe, isConnected } = useWebSocket();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [oneClickTrading, setOneClickTrading] = useState(false);
  const [favorites, setFavorites] = useState(['EUR/USD', 'XAU/USD', 'BTC/USD']);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const quoteClock = useMarketClock(instruments.some(inst=>!inst.is_virtual&&isBinancePair(inst.symbol)));

  // Fetch instruments from API
  useEffect(() => {
    const fetchInstruments = async (silent=false) => {
      try {
        if(!silent)setLoading(true);
        const token = localStorage.getItem('trader_token');
        const response = await fetch(`${API_BASE}${token ? '/api/trader/instruments' : '/api/instruments'}?limit=5000`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        
        if (response.ok) {
          const data = await response.json();
          const transformedInstruments = data.map(inst => {
            const knownPrices = {
              'EURUSD': 1.0842, 'GBPUSD': 1.2634, 'USDJPY': 149.52, 'USDCHF': 0.8845,
              'AUDUSD': 0.6523, 'USDCAD': 1.3567, 'NZDUSD': 0.5987,
              'USDTRY': 38.50, 'EURTRY': 41.75,
              'XAUUSD': 2350.00, 'XAGUSD': 28.50, 'XPTUSD': 985.00,
              'USOIL': 82.50, 'UKOIL': 86.30, 'NATGAS': 2.85,
              'BTCUSD': 67500, 'ETHUSD': 3450, 'XRPUSD': 0.52, 'SOLUSD': 145,
              'US100': 18500.0, 'US500': 5200.0, 'US30': 39500.0,
              'GER40': 18200.0, 'UK100': 8150.0, 
              // BIST Indices (TRY)
              'XU100': 10250.0, 'XU030': 10850.0, 'XUBANK': 4250.0, 'XUSIN': 8750.0,
              // Major BIST stocks (TRY)
              'GARAN': 85.50, 'AKBNK': 62.30, 'ISCTR': 18.75, 'VAKBN': 24.60,
              'THYAO': 315.00, 'EREGL': 58.40, 'KCHOL': 195.00, 'SAHOL': 78.50,
              'TUPRS': 185.00, 'ASELS': 95.00, 'SISE': 52.80, 'TCELL': 88.50,
              'BIMAS': 465.00, 'FROTO': 1250.00, 'TOASO': 385.00, 'ARCLK': 195.00,
              'PGSUS': 875.00, 'HALKB': 22.50, 'YKBNK': 32.40, 'TTKOM': 48.60,
              // European Equities (EUR / CHF / GBP)
              'ASML': 850.0, 'SAP': 215.0, 'MC.PA': 620.0, 'NESN.SW': 85.0,
              'NOVN.SW': 95.0, 'ROG.SW': 250.0, 'SIE.DE': 180.0, 'ALV.DE': 290.0,
              'BAS.DE': 45.0, 'OR.PA': 380.0, 'SAN.PA': 102.0, 'AIR.PA': 140.0,
              'BMW.DE': 80.0, 'VOW3.DE': 95.0, 'DTE.DE': 26.5, 'TTE.PA': 60.0,
              'SHEL.L': 2500.0, 'AZN.L': 12000.0, 'SAN.MC': 4.5, 'RACE.MI': 410.0,
            };
            
            const cleanSymbol = inst.symbol?.replace('/', '').replace('.IS', '').toUpperCase() || '';
            const category = inst.category || inst.type || 'stocks';
            const isBIST = category === 'bist' || inst.tradingview_symbol?.startsWith('BIST:') || inst.symbol?.endsWith('.IS');
            const isEU = category === 'eu_stocks' || category === 'stocks_eu' || inst.symbol?.endsWith('.DE') || inst.symbol?.endsWith('.PA') || inst.symbol?.endsWith('.SW') || inst.symbol?.endsWith('.AS') || inst.symbol?.endsWith('.L') || inst.symbol?.endsWith('.MC') || inst.symbol?.endsWith('.MI');
            
            let basePrice = knownPrices[cleanSymbol] || 
                           knownPrices[inst.symbol] ||
                           knownPrices[inst.symbol?.replace('.IS', '')] ||
                           null;
            
            if (basePrice === null) {
              if (category === 'forex') {
                basePrice = cleanSymbol.includes('JPY') || cleanSymbol.includes('TRY') ? 100 : 1.1;
              } else if (category === 'crypto') {
                basePrice = cleanSymbol.includes('BTC') ? 67500 : 3450;
              } else if (category === 'commodities') {
                basePrice = cleanSymbol.includes('XAU') ? 2350 : cleanSymbol.includes('OIL') ? 82 : 50;
              } else if (category === 'indices') {
                basePrice = 8000;
              } else if (isBIST) {
                // Turkish stocks - realistic TRY prices (20-500 TRY range for most stocks)
                basePrice = 50 + Math.random() * 150;
              } else if (isEU) {
                basePrice = 120;
              } else {
                basePrice = 100;
              }
            }
            
            const spread = basePrice * (inst.spread || 0.0003);
            const resolvedCategory = isBIST ? 'bist' : (isEU ? 'eu_stocks' : (inst.category || inst.type || 'stocks'));
            
            return {
              ...inst,
              id: inst.id,
              symbol: inst.symbol,
              name: inst.name,
              bid: inst.bid || basePrice,
              ask: inst.ask || (basePrice + spread),
              change: inst.change || 0,
              category: resolvedCategory,
              tradingview_symbol: inst.tradingview_symbol,
            };
          });
          
          setInstruments(transformedInstruments);
          
          const symbols = transformedInstruments.map(i => i.symbol).filter(Boolean);
          if (symbols.length > 0) {
            subscribe(symbols);
          }
        }
      } catch (error) {
        console.error('Failed to fetch instruments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstruments();
    return watchLiveRefresh(()=>fetchInstruments(true));
  }, [subscribe]);

  // Update instruments with WebSocket prices
  const getInstrumentWithLivePrice = useCallback((inst) => {
    const livePrice = scopedQuote(inst, prices[inst.symbol]);
    if (livePrice) {
      return {
        ...inst,
        bid: livePrice.bid,
        ask: livePrice.ask,
        quote_source: livePrice.source,
        manual_adjustment: livePrice.manual_adjustment,
        is_live_market: livePrice.is_live_market,
        quote_available: livePrice.quote_available,
        quote_currency: livePrice.quote_currency || inst.quote_currency,
        session_status: livePrice.session_status,
        change: livePrice.change_pct || inst.change,
      };
    }
    return !inst.is_virtual&&isBinancePair(inst.symbol)?{...inst,quote_source:'binance',is_live_market:true,quote_available:true,quote_currency:'USDT'}:inst;
  }, [prices, quoteClock]);

  const filters = [
    { id: 'all', label: t('common.all') },
    { id: 'favorites', label: '★ Favorites' },
    { id: 'forex', label: 'Forex' },
    { id: 'crypto', label: 'Crypto' },
    { id: 'indices', label: 'Indices' },
    { id: 'commodities', label: 'Commodities' },
    { id: 'stocks', label: 'US Stocks' },
    { id: 'eu_stocks', label: 'EU Stocks' },
    { id: 'bist', label: 'Turkish Equities' },
  ];

  // Filter and apply live prices
  const filteredInstruments = instruments
    .filter(inst => {
      const matchesSearch = inst.symbol?.toLowerCase().includes(search.toLowerCase()) ||
                            inst.name?.toLowerCase().includes(search.toLowerCase());
      const matchesFilter = filter === 'all' 
        || (filter === 'favorites' && favorites.includes(inst.symbol))
        || (filter === 'bist' && (inst.category === 'bist' || inst.symbol?.endsWith('.IS') || inst.tradingview_symbol?.startsWith('BIST:')))
        || (filter === 'eu_stocks' && (inst.category === 'eu_stocks' || inst.category === 'stocks_eu' || inst.symbol?.endsWith('.DE') || inst.symbol?.endsWith('.PA') || inst.symbol?.endsWith('.SW') || inst.symbol?.endsWith('.AS') || inst.symbol?.endsWith('.L') || inst.symbol?.endsWith('.MC') || inst.symbol?.endsWith('.MI')))
        || (filter === 'stocks' && (inst.category === 'stocks' || inst.category === 'stocks_us') && !['eu_stocks', 'stocks_eu'].includes(inst.category) && !inst.symbol?.endsWith('.IS') && !inst.symbol?.endsWith('.DE') && !inst.symbol?.endsWith('.PA') && !inst.symbol?.endsWith('.SW') && !inst.symbol?.endsWith('.AS') && !inst.symbol?.endsWith('.L') && !inst.symbol?.endsWith('.MC') && !inst.symbol?.endsWith('.MI'))
        || inst.category === filter;
      return matchesSearch && matchesFilter;
    })
    .map(getInstrumentWithLivePrice);

  const toggleFavorite = useCallback((symbol, e) => {
    e.stopPropagation();
    setFavorites(prev => 
      prev.includes(symbol) 
        ? prev.filter(s => s !== symbol)
        : [...prev, symbol]
    );
  }, []);

  const formatPrice = useCallback((price, symbol) => {
    if (!price || isNaN(price)) return '—';
    
    if (symbol && !symbol.includes('/') && !symbol.includes('USD') && !symbol.includes('US1') && !symbol.includes('US5')) {
      return price.toFixed(2);
    }
    
    if (symbol?.includes('JPY') || symbol?.includes('TRY')) {
      return price.toFixed(2);
    }
    if (symbol?.includes('BTC') || symbol?.includes('XAU')) {
      return price.toFixed(2);
    }
    if (symbol?.includes('US100') || symbol?.includes('US500')) {
      return price.toFixed(2);
    }
    return price.toFixed(symbol?.includes('/') && !symbol?.includes('USD/') ? 4 : 5);
  }, []);

  const handleSelectInstrument = useCallback((inst) => {
    onSelectInstrument?.(inst);
    if (isMobile || isTablet) {
      setWatchlistOpen(false);
    }
  }, [onSelectInstrument, isMobile, isTablet, setWatchlistOpen]);

  const handleInstrumentDoubleClick = useCallback((inst) => {
    onInstrumentClick?.(inst);
  }, [onInstrumentClick]);

  const handleQuickTrade = useCallback(async (inst, side, e) => {
    e.stopPropagation();
    if(tradeBusy.current)return;
    
    if (!oneClickTrading) {
      onInstrumentClick?.({ ...inst, quickTradeSide: side });
      return;
    }
    
    tradeBusy.current=true;
    try {
      const token = localStorage.getItem('trader_token');
      const price = side === 'buy' ? inst.ask : inst.bid;
      
      const response = await fetch(`${API_BASE}/api/trader/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(orderIntent(orderKey,{
          symbol: inst.symbol,
          side: side,
          type: 'market',
          volume: 0.01,
          price: price,
        })),
      });
      
      if (response.ok) {
        const result=await response.json();
        orderKey.current=null;
        toast.success(tx(`${side.toUpperCase()} ${inst.symbol} @ ${result.order.price.toFixed(5)}`));
        window.dispatchEvent(new Event('trader-data-changed'));
      } else {
        const error = await response.json();
        toast.error(tx(Array.isArray(error.detail)?error.detail.map(e=>e.msg).join('; '):error.detail || 'Order failed'));
      }
    } catch (error) {
      console.error('One-click trade error:', error);
      toast.error(tx('Failed to place order'));
    } finally {
      tradeBusy.current=false;
    }
  }, [oneClickTrading, onInstrumentClick, tx]);

  // Props to pass to WatchlistContent
  const contentProps = {
    isDark,
    t,
    search,
    setSearch,
    filter,
    setFilter,
    filters,
    oneClickTrading,
    setOneClickTrading,
    loading,
    filteredInstruments,
    isConnected,
    selectedInstrument,
    favorites,
    toggleFavorite,
    handleSelectInstrument,
    handleInstrumentDoubleClick,
    handleQuickTrade,
    formatPrice,
  };

  // Mobile/Tablet: Drawer
  if (isMobile || isTablet) {
    return (
      <>
        {/* Overlay */}
        {watchlistOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40"
            onClick={() => setWatchlistOpen(false)}
          />
        )}
        
        {/* Drawer */}
        <div 
          className={`fixed top-0 right-0 h-full z-50 transform transition-transform duration-300 ease-in-out ${
            watchlistOpen ? 'translate-x-0' : 'translate-x-full'
          } w-80 max-w-[90vw] flex flex-col ${
            isDark ? 'bg-[#0f1724]' : 'bg-white'
          }`}
        >
          {/* Header */}
          <div className={`p-4 flex items-center justify-between border-b shrink-0 ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('trading.market')}
            </span>
            <button
              onClick={() => setWatchlistOpen(false)}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-800 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
            <WatchlistContent {...contentProps} />
          </div>
        </div>
      </>
    );
  }

  // Desktop: Fixed panel with border box
  return (
    <div data-testid="desktop-market-watchlist" className={`tr-desktop-watchlist w-72 h-full flex flex-col border rounded-lg m-2 ${
      isDark ? 'bg-[#0f1724] border-gray-700' : 'bg-white border-gray-200'
    }`}>
      <WatchlistContent {...contentProps} />
    </div>
  );
};

export default MarketWatchlist;
